import moment from "moment/min/moment-with-locales";
import { withMomentLocale } from "./eventFormat";

/**
 * Chat display helpers shared by the chat list and the message thread — the
 * web counterpart of the RN `ChatContainer` / `MessageContainer` logic.
 */

export type ChatParticipant = {
  user_id: string;
  name?: string | null;
  icon?: string | null;
  is_online?: boolean | null;
  last_seen?: number | string | null;
};

export type ChatMessage = {
  id: string;
  chat_room_id: string;
  sender_id: string;
  message: string;
  type?: string | null;
  metadata?: { url?: string | null } | null;
  read_by_receiver?: boolean | null;
  ctime?: string | null;
};

export type ChatRoom = {
  id: string;
  name?: string | null;
  type?: string | null;
  coupon_id?: string | null;
  sponsor_activity_id?: string | null;
  participants?: ChatParticipant[];
  latest_message?: ChatMessage | null;
  unread_count?: number | null;
  ctime?: string | null;
  utime?: string | null;
};

/** Context badge shown next to the room name, keyed by the backend room type. */
export const CHAT_TYPE_LABEL_KEYS: Record<string, string> = {
  single: "Chat",
  coupon: "Coupon",
  sponsor: "Sponsor",
  admin: "Admin",
  group: "Group",
};

export const isGroupRoom = (room?: ChatRoom | null) => room?.type === "group";

/** The other participant of a one-to-one room (single / coupon / sponsor / admin). */
export const getRoomPeer = (
  room: ChatRoom | null | undefined,
  currentUserId: string,
): ChatParticipant | null =>
  (room?.participants ?? []).find(
    (participant) => participant.user_id !== currentUserId,
  ) ?? null;

export const getRoomTitle = (
  room: ChatRoom | null | undefined,
  currentUserId: string,
) => {
  if (isGroupRoom(room)) return room?.name || "";
  return getRoomPeer(room, currentUserId)?.name || room?.name || "";
};

export const getRoomIcon = (
  room: ChatRoom | null | undefined,
  currentUserId: string,
) => (isGroupRoom(room) ? null : getRoomPeer(room, currentUserId)?.icon || null);

/**
 * Room list timestamp: the clock time for today's messages, a relative
 * "x minutes ago" label for anything older.
 */
export const formatRoomTime = (
  ctime?: string | null,
  language?: string,
) => {
  if (!ctime) return "";
  withMomentLocale(language ?? "en");
  const parsed = moment(ctime);
  if (!parsed.isValid()) return "";
  if (parsed.isSame(moment(), "day")) return parsed.format("HH:mm");
  return parsed.fromNow();
};

export const formatMessageTime = (ctime?: string | null) => {
  if (!ctime) return "";
  const parsed = moment(ctime);
  return parsed.isValid() ? parsed.format("HH:mm") : "";
};

export const getMessageDayKey = (ctime?: string | null) =>
  ctime ? moment(ctime).format("YYYY-MM-DD") : "";

/** Newest conversation first — the ordering the backend already applies. */
export const sortRoomsByLatestMessage = (rooms: ChatRoom[]) =>
  [...rooms].sort((a, b) => {
    const timeA = a.latest_message?.ctime
      ? new Date(a.latest_message.ctime).getTime()
      : 0;
    const timeB = b.latest_message?.ctime
      ? new Date(b.latest_message.ctime).getTime()
      : 0;
    return timeB - timeA;
  });
